import React from 'react';
import type {ColDef, Column, HeaderPosition, ICellRendererParams, TabToNextHeaderParams} from 'ag-grid-community';
import {CircleAlert} from 'lucide-react';
import Checkbox from 'components/common/checkbox.component';
import {ICON_SIZE} from 'components/common/constants';
import {Tooltip} from 'components/tooltip/Tooltip';
import {ROW_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {DOCUMENT_TYPE, TABLE_COLUMNS_TYPES} from 'utils/constants';
import {formatAmountValue, formatDocumentType} from 'utils/valueFormatter.function';
import type {Translate} from 'types/myTasks';
import type {ColumnMapEntry, DataGridRow, DataGridSortingLogic} from 'types/appDataGrid';
import {AppDataGridFilter} from './AppDataGridFilter.component';
import {deputyCaption} from './deputy.function';

// the narrowest a column that shares out the leftover space is allowed to get
const MIN_COL_WIDTH = 70;

// the clear-all button's own column, which no column map declares
export const CLEAR_ALL_COL = 'clear_all_search';
const CLEAR_ALL_WIDTH = 48;

// a status the backend reports as a problem rather than a stage
const ERROR = 'ERROR';
const BLOCKED = 'BLOCKED';

/** The header only ever flips between the two, never back to unsorted, as the legacy table had it. */
export const SORTING_ORDER: ('asc' | 'desc')[] = ['asc', 'desc'];

const isFilterable = (type: string) => type !== TABLE_COLUMNS_TYPES.NONE
    && type !== TABLE_COLUMNS_TYPES.TASK_STATUS;

// there is nothing to order a list of checkboxes by
const isSortable = (type: string) => type !== TABLE_COLUMNS_TYPES.CHECKBOX;

/** A checkbox whose cell the grid knows to keep off the row's own click (see AppDataGrid). */
const selectRenderer = (taskSelect: (index: number) => void, translate: Translate) =>
    ({data}: ICellRendererParams<DataGridRow>) => {
        if (!data) return null;

        // the box fills the cell, so the checkbox alone carries the click - a handler on the
        // wrapper would run a second time on the click the label forwards to its input
        const select = () => taskSelect(data.index as number);

        return (
            <div className="app-data-grid__select">
                <Checkbox inputId={String(data.index)}
                          ariaLabel={translate('myTasks.task.select')}
                          onChange={select}
                          isChecked={!!data.isSelected}/>
            </div>
        );
    };

/** The document type as its glyph. Its tooltip is unconditional - the icon has no text to crop. */
const documentTypeRenderer = (translate: Translate) =>
    ({data}: ICellRendererParams<DataGridRow>) => {
        if (!data) return null;

        const label = translate('documentType.' + DOCUMENT_TYPE.asString(data.documentType as number));
        const tooltipId = 'data-grid-doctype-tooltip-' + data.index;

        return (
            <span className={'d-block text-center ' + formatDocumentType(data.documentType, data.amount)}
                  role="img"
                  aria-label={label}
                  data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID} data-place={ROW_TOOLTIP_PLACE}>
                <Tooltip id={tooltipId} content={() => label}/>
            </span>
        );
    };

/** A coloured marker, or an alert glyph where the task is stuck. */
const taskStatusRenderer = (translate: Translate) =>
    ({data}: ICellRendererParams<DataGridRow>) => {
        if (!data) return null;

        const status = data.taskOverviewStatus as string;
        const label = translate('taskOverview.status.' + status);
        const tooltipId = 'data-grid-status-tooltip-' + data.index;

        return (
            <div className="text-center"
                 role="img" aria-label={label}
                 data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID} data-place={ROW_TOOLTIP_PLACE}>
                {status === ERROR || status === BLOCKED
                    ? <CircleAlert size={ICON_SIZE.SMALL} style={{color: 'var(--ga-color-icon-error)'}}/>
                    : <span className={'task-' + status}/>}
                <Tooltip id={tooltipId} content={() => label}/>
            </div>
        );
    };

const amountRenderer = ({data, colDef}: ICellRendererParams<DataGridRow>) => {
    const value = data?.[colDef?.field] as number;

    return <span className={Number(value) < 0 ? 'text-danger' : undefined}>{formatAmountValue(value)}</span>;
};

/** Whether the approver stands in as a deputy, in words, as the legacy table read it. */
const deputyRenderer = (translate: Translate) =>
    ({data, colDef}: ICellRendererParams<DataGridRow>) =>
        data ? translate(deputyCaption(data[colDef?.field])) : null;

/** A column whose value is already a node the page built. */
const customRenderer = ({data, colDef}: ICellRendererParams<DataGridRow>) =>
    (data?.[colDef?.field] ?? null) as React.ReactNode;

function cellRenderer(entry: ColumnMapEntry, translate: Translate, taskSelect?: (index: number) => void) {
    switch (entry.type) {
        case TABLE_COLUMNS_TYPES.CHECKBOX:
            return taskSelect ? selectRenderer(taskSelect, translate) : undefined;
        case TABLE_COLUMNS_TYPES.SELECT:
        case TABLE_COLUMNS_TYPES.PROCESS_SELECT:
            return documentTypeRenderer(translate);
        case TABLE_COLUMNS_TYPES.TASK_STATUS:
            return taskStatusRenderer(translate);
        case TABLE_COLUMNS_TYPES.AMOUNT:
            return amountRenderer;
        case TABLE_COLUMNS_TYPES.DEPUTY_SELECT:
            return deputyRenderer(translate);
        case 'custom':
            return customRenderer;
        // everything else is plain text, which ag-grid draws itself - and only text it drew
        // itself can it spot the truncation of, which is what the tooltip hangs off
        default:
            return undefined;
    }
}

export interface ColumnBuildOptions {
    translate: Translate;
    /** columns are sortable only where the page wired the sort up */
    sortable: boolean;
    sortingLogic?: DataGridSortingLogic;
    showFilters: boolean;
    taskSelect?: (index: number) => void;
}

/**
 * The ag-grid columns one `columnsMap` list asks for.
 *
 * A map entry carrying a width keeps it; the rest share out what is left, which is what the
 * legacy table did by hand (see computeColumnWidth in Table.component).
 */
export function buildColumnDefs(entries: ColumnMapEntry[], options: ColumnBuildOptions): ColDef<DataGridRow>[] {
    const {translate, sortable, sortingLogic, showFilters, taskSelect} = options;
    const filterable = entries.some(entry => isFilterable(entry.type));

    const columns: ColDef<DataGridRow>[] = entries.map((entry, index) => {
        const fixedWidth = entry.width;
        const sortDirection = sortingLogic?.[String(index)]?.direction;

        return {
            colId: entry.searchValue || entry.value || String(index),
            field: entry.value,
            headerName: translate(entry.caption),
            ...(fixedWidth ? {width: fixedWidth, minWidth: fixedWidth} : {flex: 1, minWidth: MIN_COL_WIDTH}),
            ...(entry.align === 'right' ? {type: 'rightAligned'} : {}),
            sortable: sortable && isSortable(entry.type),
            sortingOrder: SORTING_ORDER,
            ...(sortable && sortDirection ? {sort: sortDirection} : {}),
            // the value is readable in the cell; only a clipped one needs spelling out, which is
            // what AppGrid's whenTruncated mode decides
            ...(entry.showTooltipWhenCropped ? {tooltipField: entry.value} : {}),
            cellRenderer: cellRenderer(entry, translate, taskSelect),
            // the filter row is ours to fill and the backend does the filtering, so ag-grid's
            // own filter never runs - and none of the three buttons that would open it belongs:
            // the funnel in the heading, the one in the filter cell, or the heading's menu. A
            // column with nothing to filter by gets no filter at all, which is what keeps Tab
            // from stopping on its empty cell
            filter: showFilters && isFilterable(entry.type),
            floatingFilter: showFilters && isFilterable(entry.type),
            suppressHeaderFilterButton: true,
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            floatingFilterComponent: AppDataGridFilter,
            floatingFilterComponentParams: {entry}
        };
    });

    // the clear-all button rides in a column of its own, as the legacy table had it
    if (showFilters && filterable) {
        columns.push({
            colId: CLEAR_ALL_COL,
            headerName: '',
            width: CLEAR_ALL_WIDTH,
            minWidth: CLEAR_ALL_WIDTH,
            maxWidth: CLEAR_ALL_WIDTH,
            sortable: false,
            resizable: false,
            filter: true,
            floatingFilter: true,
            suppressHeaderFilterButton: true,
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            floatingFilterComponent: AppDataGridFilter,
            floatingFilterComponentParams: {
                entry: {type: TABLE_COLUMNS_TYPES.CLEAR_ALL_FILTERS, searchValue: CLEAR_ALL_COL}
            }
        });
    }

    return columns;
}

const hasFloatingFilter = (column: Column) => !!column.getColDef().floatingFilter;

/**
 * Tab past the stops that hold nothing to focus.
 *
 * The clear-all column's heading is empty, so stopping on it reads as nothing; the button lives
 * in the filter cell under it, which keeps its place in the tab order. Forward, the stop after
 * that heading - the last of its row - is the start of the next row; backwards, it is the column
 * before it on the same row.
 *
 * The filter cell of a column with nothing to filter by is empty too, and is passed over the
 * same way: forward to the next cell holding a filter, backwards to the one before it - or, with
 * none left before it, to the last heading.
 */
export function skipEmptyHeaders(params: TabToNextHeaderParams<DataGridRow>): HeaderPosition | boolean {
    const {nextHeaderPosition: next, headerRowCount, backwards, api} = params;
    const filterRowIndex = headerRowCount - 1;

    if (!next) {
        return false;
    }

    const columns = api.getAllDisplayedColumns();
    // no filter row at all - the headings are the only row, and nothing in it is empty
    if (!columns.some(hasFloatingFilter)) {
        return next;
    }

    const lastHeading = () => {
        const before = columns[columns.findIndex(column => column.getColId() === CLEAR_ALL_COL) - 1];
        return before ? {headerRowIndex: filterRowIndex - 1, column: before} : false;
    };

    // a heading: only the clear-all one is skipped
    if (next.headerRowIndex < filterRowIndex) {
        if (next.column.getUniqueId() !== CLEAR_ALL_COL) {
            return next;
        }
        if (backwards) {
            return lastHeading();
        }
        if (columns.length === 0) {
            return false;
        }
        // the first filter cell can be an empty one itself, so it goes through the check below
        return skipEmptyHeaders({...params, nextHeaderPosition: {headerRowIndex: filterRowIndex, column: columns[0]}});
    }

    const column = next.column as Column;
    if (hasFloatingFilter(column)) {
        return next;
    }

    const from = columns.indexOf(column);
    const filled = (backwards ? columns.slice(0, from).reverse() : columns.slice(from + 1)).find(hasFloatingFilter);
    if (filled) {
        return {headerRowIndex: filterRowIndex, column: filled};
    }
    return backwards ? lastHeading() : false;
}

/**
 * The sort the backend should run, in the shape its actions already take: `sort` is what goes
 * on the wire, `sortBy` the per-column state the reducers hold (reactabular's, keyed by the
 * column's position in the map).
 */
export function buildSortFilters<TFilters extends object>(
    filters: TFilters,
    entries: ColumnMapEntry[],
    sortedColId: string | null,
    direction: 'asc' | 'desc' | null
): TFilters {
    const index = entries.findIndex(entry => (entry.searchValue || entry.value) === sortedColId);

    if (index === -1 || !direction) {
        return {...filters, sort: undefined, sortBy: {}};
    }

    return {
        ...filters,
        sort: {direction, index: entries[index].searchValue},
        sortBy: {[index]: {direction, position: 0}}
    };
}
